import { Sentry } from "@/telemetry/sentry"
import type { ImageType } from "@/core/export-image"
import { getTIFFEncodedByteLength } from "@/core/encoders/tiff-encoder"

export type ValidatedExportImageType = Extract<ImageType, "jpg" | "tif" | "psd">
const PSD_HEADER_BYTE_LENGTH = 74

// Calibrated from the 1,377-file diverse RAW run at quality 90. The model uses
// the 55th-percentile fit so it has a small positive bias without approaching a
// content-independent worst case. ISO is the only capture value that improved
// held-out-camera validation; source RAW byte density did not generalize.
export const JPEG_SIZE_ESTIMATION_PARAMETERS = {
	model: "calibrated-libjpeg-metadata-bpp",
	version: 3,
	mcuSize: 16,
	fixedOverheadBytes: 32_768,
	calibrationFileCount: 1_377,
	calibrationQualityPercent: 90,
	calibrationPercentile: 0.55,
	quality90BytesPerPixelAtIso100: 0.25,
	quality90QuantizationScale: 20,
	qualityScaleExponent: 0.5,
	isoReference: 100,
	isoExponent: 0.055,
	minimumCalibrationIso: 10,
	maximumCalibrationIso: 20_000,
	missingIsoMultiplier: 1.24,
	minBytesPerPixel: 0.06,
	maxEstimatedBytesPerPixel: 1.2,
} as const

export interface JpegSizeEstimationParameters {
	mcuSize: number
	fixedOverheadBytes: number
	quality90BytesPerPixelAtIso100: number
	quality90QuantizationScale: number
	qualityScaleExponent: number
	isoReference: number
	isoExponent: number
	minimumCalibrationIso: number
	maximumCalibrationIso: number
	missingIsoMultiplier: number
	minBytesPerPixel: number
	maxEstimatedBytesPerPixel: number
}

export function isValidatedExportType(type: ImageType): type is ValidatedExportImageType {
	return type === "jpg" || type === "tif" || type === "psd"
}

export interface EncodedSizeCheckInput {
	format: ValidatedExportImageType
	encodedBytes: number
	width: number
	height: number
	quality?: number
	iso?: number | null
	bitDepth?: 8 | 16
	colorSpace?: PredefinedColorSpace
}

export interface JpegSizeEstimationDetails {
	qualityInteger: number
	quantizationScale: number
	qualityMultiplier: number
	isoUsed: number | null
	isoMultiplier: number
	bytesPerPaddedPixel: number
	paddedWidth: number
	paddedHeight: number
	paddedPixelCount: number
	estimatedBytes: number
}

export interface EncodedSizeCheckResult {
	isSuspicious: boolean
	estimatedOutputBytes: number
	minAcceptableBytes: number
	sizeRatio: number
	model: string
}

export interface EncodedSizeValidationAndReportInput {
	format: ValidatedExportImageType
	encoded: unknown
	width: number
	height: number
	quality?: number
	iso?: number | null
	bitDepth?: 8 | 16
	photoId?: string
	colorSpace?: PredefinedColorSpace
	sourceFileSize?: number
	sourceFileExtension?: string
	extra?: Record<string, unknown>
}

export function estimateEncodedExportBytes(
	input: Omit<EncodedSizeCheckInput, "encodedBytes">,
): number {
	const pixelCount = Math.max(1, input.width * input.height)

	if (input.format === "jpg") {
		return getJpegSizeEstimationDetails(input).estimatedBytes
	}

	const bitsPerChannel = input.bitDepth ?? 8
	if (input.format === "tif") {
		return getTIFFEncodedByteLength({
			width: input.width,
			height: input.height,
			bitDepth: bitsPerChannel,
			colorSpace: input.colorSpace,
		})
	}

	// PSD in this codebase is uncompressed RGB export data plus a small header.
	const rawRgbBytes = Math.round(pixelCount * 3 * (bitsPerChannel / 8))
	return rawRgbBytes + PSD_HEADER_BYTE_LENGTH
}

export function getJpegSizeEstimationDetails(
	input: Pick<EncodedSizeCheckInput, "width" | "height" | "quality" | "iso">,
	parameters: JpegSizeEstimationParameters = JPEG_SIZE_ESTIMATION_PARAMETERS,
): JpegSizeEstimationDetails {
	const qualityInteger = Math.round(Math.min(1, Math.max(0.01, input.quality ?? 1)) * 100)
	const quantizationScale = qualityInteger < 50 ? 5000 / qualityInteger : 200 - qualityInteger * 2
	const qualityMultiplier = Math.pow(
		parameters.quality90QuantizationScale / Math.max(1, quantizationScale),
		parameters.qualityScaleExponent,
	)
	const candidateIso = input.iso
	const isoUsed =
		typeof candidateIso === "number" && Number.isFinite(candidateIso) && candidateIso > 0
			? Math.min(
					parameters.maximumCalibrationIso,
					Math.max(parameters.minimumCalibrationIso, candidateIso),
				)
			: null
	const isoMultiplier =
		isoUsed === null
			? parameters.missingIsoMultiplier
			: Math.pow(isoUsed / parameters.isoReference, parameters.isoExponent)
	const bytesPerPaddedPixel = Math.min(
		parameters.maxEstimatedBytesPerPixel,
		Math.max(
			parameters.minBytesPerPixel,
			parameters.quality90BytesPerPixelAtIso100 * qualityMultiplier * isoMultiplier,
		),
	)
	const paddedWidth =
		Math.ceil(Math.max(1, input.width) / parameters.mcuSize) * parameters.mcuSize
	const paddedHeight =
		Math.ceil(Math.max(1, input.height) / parameters.mcuSize) * parameters.mcuSize
	const paddedPixelCount = paddedWidth * paddedHeight
	const estimatedBytes = Math.max(
		1,
		Math.round(paddedPixelCount * bytesPerPaddedPixel + parameters.fixedOverheadBytes),
	)

	return {
		qualityInteger,
		quantizationScale,
		qualityMultiplier,
		isoUsed,
		isoMultiplier,
		bytesPerPaddedPixel,
		paddedWidth,
		paddedHeight,
		paddedPixelCount,
		estimatedBytes,
	}
}

export function checkEncodedExportSize(input: EncodedSizeCheckInput): EncodedSizeCheckResult {
	const pixelCount = Math.max(1, input.width * input.height)
	const estimatedOutputBytes = estimateEncodedExportBytes(input)

	if (input.format === "jpg") {
		// 0.02 means "less than 2% of estimated output is suspicious", with a hard floor.
		const suspiciousSizeRatioThreshold = 0.02
		const minAcceptableBytes = Math.max(
			512,
			Math.round(estimatedOutputBytes * suspiciousSizeRatioThreshold),
		)

		return {
			isSuspicious: input.encodedBytes < minAcceptableBytes,
			estimatedOutputBytes,
			minAcceptableBytes,
			sizeRatio: input.encodedBytes / Math.max(1, estimatedOutputBytes),
			model: JPEG_SIZE_ESTIMATION_PARAMETERS.model,
		}
	}

	const bitsPerChannel = input.bitDepth ?? 8
	const rawRgbBytes = Math.round(pixelCount * 3 * (bitsPerChannel / 8))
	const minAcceptableBytes = Math.max(1024, Math.round(rawRgbBytes * 0.85))

	return {
		isSuspicious: input.encodedBytes < minAcceptableBytes,
		estimatedOutputBytes,
		minAcceptableBytes,
		sizeRatio: input.encodedBytes / Math.max(1, estimatedOutputBytes),
		model: "uncompressed-rgb",
	}
}

export function getEncodedExportByteLength(value: unknown) {
	if (typeof value === "string") {
		return new TextEncoder().encode(value).byteLength
	}

	if (value instanceof Blob) {
		return value.size
	}

	const sizeLikeByteLength = getSizeLikeByteLength(value)
	if (sizeLikeByteLength !== null) {
		return sizeLikeByteLength
	}

	if (value instanceof ArrayBuffer) {
		return value.byteLength
	}

	if (ArrayBuffer.isView(value)) {
		return value.byteLength
	}

	return 0
}

function getSizeLikeByteLength(value: unknown) {
	if (!value || typeof value !== "object" || !("size" in value)) {
		return null
	}

	return typeof value.size === "number" ? value.size : null
}

export function validateEncodedExportSizeAndReport(
	input: EncodedSizeValidationAndReportInput,
): EncodedSizeCheckResult {
	const encodedBytes = getEncodedExportByteLength(input.encoded)
	const sizeCheck = checkEncodedExportSize({
		format: input.format,
		encodedBytes,
		width: input.width,
		height: input.height,
		quality: input.quality,
		iso: input.iso,
		bitDepth: input.bitDepth,
		colorSpace: input.colorSpace,
	})

	if (!sizeCheck.isSuspicious) {
		return sizeCheck
	}

	const error = new Error(`Encoded ${input.format.toUpperCase()} output is suspiciously small`)
	console.error(error, { ...input, encodedBytes, sizeCheck })
	Sentry.captureException(error, {
		tags: {
			photoId: input.photoId ?? "unknown",
			exportFormat: input.format,
			exportColorSpace: input.colorSpace,
		},
		extra: {
			encodedBytes,
			width: input.width,
			height: input.height,
			quality: input.quality,
			iso: input.iso,
			bitDepth: input.bitDepth,
			sourceFileSize: input.sourceFileSize,
			sourceFileExtension: input.sourceFileExtension,
			sizeCheck,
			...(input.extra ?? {}),
		},
	})

	return sizeCheck
}
